/**
 * Color palette definitions for the FTL design system.
 *
 * Each palette defines brand colors (50–900), sidebar colors, and a focus ring
 * color. The CSS custom properties in index.css are overridden at runtime by
 * setting these values on `document.documentElement.style`.
 *
 * Palettes are identified by slug (e.g. "ocean-blue"). The slug is stored in
 * `platform_settings` (category: branding, key: color_palette) and resolved
 * per-org via the standard settings hierarchy.
 */

export interface PaletteDefinition {
  id: string;
  name: string;
  description: string;
  /** Preview swatch colors shown in the palette picker (brand-500, brand-700) */
  preview: [string, string];
  brand: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  };
  sidebar: {
    bg: string;
    hover: string;
    active: string;
    text: string;
    textActive: string;
  };
  focus: string;
}

// ---------------------------------------------------------------------------
// Palette definitions
// ---------------------------------------------------------------------------

export const PALETTES: Record<string, PaletteDefinition> = {
  "ocean-blue": {
    id: "ocean-blue",
    name: "Ocean Blue",
    description: "Classic professional blue — the default FTL theme",
    preview: ["#3b82f6", "#1d4ed8"],
    brand: {
      50: "#eff6ff",
      100: "#dbeafe",
      200: "#bfdbfe",
      300: "#93c5fd",
      400: "#60a5fa",
      500: "#3b82f6",
      600: "#2563eb",
      700: "#1d4ed8",
      800: "#1e40af",
      900: "#1e3a8a",
    },
    sidebar: {
      bg: "#0f172a",
      hover: "#1e293b",
      active: "#1e3a8a",
      text: "#cbd5e1",
      textActive: "#ffffff",
    },
    focus: "#3b82f6",
  },

  emerald: {
    id: "emerald",
    name: "Emerald",
    description: "Fresh and modern green for growth-focused teams",
    preview: ["#10b981", "#047857"],
    brand: {
      50: "#ecfdf5",
      100: "#d1fae5",
      200: "#a7f3d0",
      300: "#6ee7b7",
      400: "#34d399",
      500: "#10b981",
      600: "#059669",
      700: "#047857",
      800: "#065f46",
      900: "#064e3b",
    },
    sidebar: {
      bg: "#022c22",
      hover: "#064e3b",
      active: "#047857",
      text: "#a7f3d0",
      textActive: "#ffffff",
    },
    focus: "#10b981",
  },

  violet: {
    id: "violet",
    name: "Violet",
    description: "Bold and creative purple palette",
    preview: ["#8b5cf6", "#6d28d9"],
    brand: {
      50: "#f5f3ff",
      100: "#ede9fe",
      200: "#ddd6fe",
      300: "#c4b5fd",
      400: "#a78bfa",
      500: "#8b5cf6",
      600: "#7c3aed",
      700: "#6d28d9",
      800: "#5b21b6",
      900: "#4c1d95",
    },
    sidebar: {
      bg: "#1e1036",
      hover: "#2e1065",
      active: "#6d28d9",
      text: "#c4b5fd",
      textActive: "#ffffff",
    },
    focus: "#8b5cf6",
  },

  rose: {
    id: "rose",
    name: "Rose",
    description: "Warm and inviting pink-red palette",
    preview: ["#f43f5e", "#be123c"],
    brand: {
      50: "#fff1f2",
      100: "#ffe4e6",
      200: "#fecdd3",
      300: "#fda4af",
      400: "#fb7185",
      500: "#f43f5e",
      600: "#e11d48",
      700: "#be123c",
      800: "#9f1239",
      900: "#881337",
    },
    sidebar: {
      bg: "#1a0a10",
      hover: "#2d1220",
      active: "#be123c",
      text: "#fda4af",
      textActive: "#ffffff",
    },
    focus: "#f43f5e",
  },

  slate: {
    id: "slate",
    name: "Slate",
    description: "Minimal and neutral for distraction-free work",
    preview: ["#64748b", "#334155"],
    brand: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    sidebar: {
      bg: "#0f172a",
      hover: "#1e293b",
      active: "#334155",
      text: "#cbd5e1",
      textActive: "#ffffff",
    },
    focus: "#64748b",
  },

  teal: {
    id: "teal",
    name: "Teal",
    description: "Calm and balanced blue-green palette",
    preview: ["#14b8a6", "#0f766e"],
    brand: {
      50: "#f0fdfa",
      100: "#ccfbf1",
      200: "#99f6e4",
      300: "#5eead4",
      400: "#2dd4bf",
      500: "#14b8a6",
      600: "#0d9488",
      700: "#0f766e",
      800: "#115e59",
      900: "#134e4a",
    },
    sidebar: {
      bg: "#0a2725",
      hover: "#134e4a",
      active: "#0f766e",
      text: "#99f6e4",
      textActive: "#ffffff",
    },
    focus: "#14b8a6",
  },
};

/** Ordered list of palette IDs for display */
export const PALETTE_ORDER: string[] = [
  "ocean-blue",
  "emerald",
  "violet",
  "rose",
  "teal",
  "slate",
];

/** Default palette when no preference is set */
export const DEFAULT_PALETTE_ID = "ocean-blue";

/**
 * Create a custom palette definition from a generated color ramp.
 */
export function createCustomPalette(
  primaryHex: string,
  ramp: { brand: PaletteDefinition["brand"]; sidebar: PaletteDefinition["sidebar"]; focus: string },
): PaletteDefinition {
  return {
    id: `custom-${primaryHex.replace("#", "")}`,
    name: "Custom",
    description: `Custom palette based on ${primaryHex}`,
    preview: [ramp.brand[500], ramp.brand[700]],
    brand: ramp.brand,
    sidebar: ramp.sidebar,
    focus: ramp.focus,
  };
}

/**
 * Apply a palette's CSS custom properties to the document root.
 * Accepts either a palette ID (string) or a full PaletteDefinition object.
 */
export function applyPalette(paletteOrId: string | PaletteDefinition): void {
  const palette = typeof paletteOrId === "string"
    ? (PALETTES[paletteOrId] ?? PALETTES[DEFAULT_PALETTE_ID])
    : paletteOrId;
  const root = document.documentElement;

  // Brand colors
  const brandKeys = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
  for (const shade of brandKeys) {
    root.style.setProperty(`--color-brand-${shade}`, palette.brand[shade]);
  }

  // Sidebar colors
  root.style.setProperty("--color-sidebar", palette.sidebar.bg);
  root.style.setProperty("--color-sidebar-hover", palette.sidebar.hover);
  root.style.setProperty("--color-sidebar-active", palette.sidebar.active);
  root.style.setProperty("--color-sidebar-text", palette.sidebar.text);
  root.style.setProperty("--color-sidebar-text-active", palette.sidebar.textActive);

  // Focus ring
  root.style.setProperty("--color-border-focus", palette.focus);

  // Info color mirrors brand (for info badges).
  // NOTE: We only override --color-info (the accent). We do NOT override
  // --color-info-bg here because that property has separate light/dark values
  // in the CSS @theme block. Setting it to brand[50] would break dark mode
  // (light background on dark surfaces).
  root.style.setProperty("--color-info", palette.brand[500]);
}

/**
 * Reset palette to defaults (remove runtime overrides, letting @theme values apply).
 * Used when switching back to the default ocean-blue palette.
 */
export function resetPalette(): void {
  const root = document.documentElement;
  const props = [
    "--color-brand-50", "--color-brand-100", "--color-brand-200", "--color-brand-300",
    "--color-brand-400", "--color-brand-500", "--color-brand-600", "--color-brand-700",
    "--color-brand-800", "--color-brand-900",
    "--color-sidebar", "--color-sidebar-hover", "--color-sidebar-active",
    "--color-sidebar-text", "--color-sidebar-text-active",
    "--color-border-focus", "--color-info",
  ];
  for (const prop of props) {
    root.style.removeProperty(prop);
  }
}
